import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { MapPin } from 'lucide-react'
import { MerchantCard } from './MerchantCard'
import { merchantApi } from '@/services/api'

interface MerchantsListProps {
  merchants: any[]
  isLoading: boolean
  searchTerm: string
  selectedCategory: string
  onRefetch: () => void
}

export function MerchantsList({
  merchants,
  isLoading,
  searchTerm,
  selectedCategory,
  onRefetch,
}: MerchantsListProps) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
              <div className="flex items-center space-x-3">
                <div className="w-16 h-16 bg-gray-200 rounded-xl" />
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-2/3 mb-2" />
                  <div className="h-3 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (merchants && merchants.length > 0) {
    return (
      <div className="p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3"
        >
          {merchants.map((merchant: any, index: number) => (
            <MerchantCard 
              key={merchant.id} 
              merchant={merchant} 
              delay={index * 0.1}
              onToggleFavorite={async (m) => {
                try {
                  const res = await merchantApi.getFavorites(m.id)
                  const list = res.data.data || res.data
                  if (Array.isArray(list) && list.length > 0) {
                    await merchantApi.removeFavoriteById(list[0].id)
                  } else {
                    await merchantApi.addFavorite(m.id)
                  }
                  onRefetch()
                } catch (e) {
                  console.error('favorite toggle failed', e)
                }
              }}
              onAddReview={async (m) => {
                const ratingStr = window.prompt('Avalie de 1 a 5:')
                if (!ratingStr) return
                const rating = Number(ratingStr)
                if (Number.isNaN(rating) || rating < 1 || rating > 5) return
                const comment = window.prompt('Comentário (opcional):') || undefined
                try {
                  await merchantApi.createReview({ merchant_location: m.id, rating, comment })
                  onRefetch()
                } catch (e) {
                  console.error('create review failed', e)
                }
              }}
            />
          ))}
        </motion.div>
      </div>
    )
  }

  return (
    <div className="p-4">
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <MapPin className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-text-primary mb-2">
          {t('merchants.noMerchants')}
        </h3>
        <p className="text-text-secondary">
          {searchTerm || selectedCategory !== 'all' 
            ? t('merchants.adjustFilters')
            : t('merchants.noMerchantsInRegion')
          }
        </p>
      </div>
    </div>
  )
}
